import { AuthError, type AuthErrorCode, type AuthSession } from './types';

interface ErrorPayload {
  readonly error?: string;
  readonly message?: string;
}

const KNOWN_ERROR_CODES = new Set<AuthErrorCode>([
  'account_inactive',
  'account_not_provisioned',
  'invalid_credentials',
  'invalid_request',
  'too_many_attempts',
  'service_unavailable',
]);

function errorCode(value: unknown, fallback: AuthErrorCode): AuthErrorCode {
  return typeof value === 'string' && KNOWN_ERROR_CODES.has(value as AuthErrorCode)
    ? (value as AuthErrorCode)
    : fallback;
}

export class AuthClient {
  private readonly baseUrl: string;

  constructor(baseUrl = import.meta.env.VITE_AUTH_API_BASE_URL ?? '') {
    this.baseUrl = baseUrl.trim().replace(/\/$/, '');
  }

  async session(): Promise<AuthSession | null> {
    const response = await this.request('/api/auth/session');
    if (response.status === 401) return null;
    if (!response.ok) throw await this.toError(response, 'service_unavailable');
    return (await this.readJson(response)) as AuthSession;
  }

  async signInWithCredentials(email: string, password: string): Promise<AuthSession> {
    const response = await this.request('/api/auth/signin/credentials', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) throw await this.toError(response, 'invalid_credentials');
    return (await this.readJson(response)) as AuthSession;
  }

  async signOut(): Promise<void> {
    const response = await this.request('/api/auth/signout', { method: 'POST' });
    if (!response.ok) throw await this.toError(response, 'service_unavailable');
  }

  private url(path: string): URL {
    const base = this.baseUrl ? `${this.baseUrl}/` : window.location.origin;
    return new URL(path.replace(/^\//, ''), base);
  }

  private request(path: string, init: RequestInit = {}): Promise<Response> {
    return fetch(this.url(path), {
      ...init,
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...init.headers,
      },
    }).catch(() => {
      throw new AuthError('service_unavailable', 'The sign-in service is unavailable.');
    });
  }

  private async readJson(response: Response): Promise<unknown> {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      throw new AuthError('service_unavailable', 'The sign-in service returned an invalid response.');
    }
    return response.json();
  }

  private async toError(response: Response, fallback: AuthErrorCode): Promise<AuthError> {
    let payload: ErrorPayload = {};
    try {
      payload = (await this.readJson(response)) as ErrorPayload;
    } catch {
      return new AuthError(fallback, 'Sign-in could not be completed.');
    }
    return new AuthError(
      errorCode(payload.error, fallback),
      payload.message ?? 'Sign-in could not be completed.'
    );
  }
}
